import { useState, useEffect, useMemo } from 'react';
import { 
  Plus, Pencil, Trash2, Search, Star, Zap, TrendingUp, 
  Copy, MoreVertical, Filter, RefreshCw, ChevronLeft, ChevronRight,
  ArrowUpDown, ArrowUp, ArrowDown, Eye, EyeOff, Clock, Image as ImageIcon, X,
  Link as LinkIcon, Loader2, Calendar, CheckCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import AdminLayout from '@/components/admin/AdminLayout';
import { productsAPI, categoriesAPI, platformsAPI, uploadImage } from '@/lib/api';
import { toast } from 'sonner';

const defaultPlatforms = [
  { slug: 'shopee', name: 'Shopee', color: '#EE4D2D' },
  { slug: 'mercadolivre', name: 'Mercado Livre', color: '#FFE600' },
  { slug: 'temu', name: 'Temu', color: '#FB7701' },
  { slug: 'amazon', name: 'Amazon', color: '#FF9900' },
  { slug: 'shein', name: 'Shein', color: '#000000' },
  { slug: 'kabum', name: 'Kabum', color: '#FF6500' },
  { slug: 'casasbahia', name: 'Casas Bahia', color: '#0066CC' },
  { slug: 'americanas', name: 'Americanas', color: '#E60014' },
  { slug: 'hotmart', name: 'Hotmart', color: '#F04E23' },
];

const formatPrice = (price) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(price);
};

// Parse Brazilian price: "2.299,90" -> 2299.90, "1.251" -> 1251, "29,90" -> 29.90
const parsePriceBR = (value) => {
  if (!value) return 0;
  let str = String(value).trim();
  // If has both dot and comma: "2.299,90" -> Brazilian format
  if (str.includes('.') && str.includes(',')) {
    str = str.replace(/\./g, '').replace(',', '.');
  } 
  // If only comma: "29,90" -> decimal comma
  else if (str.includes(',')) {
    str = str.replace(',', '.');
  }
  // If only dot: check if it's thousands separator "2.299" or decimal "2.30"
  else if (str.includes('.')) {
    const parts = str.split('.');
    // "2.299" -> 3 digits after dot = thousands separator, not decimal
    if (parts.length === 2 && parts[1].length === 3) {
      str = str.replace('.', '');
    }
    // "2.30" or "29.90" -> 2 digits after dot = decimal (keep as is)
  }
  const num = parseFloat(str);
  return isNaN(num) ? 0 : num;
};

// Format number for display in input (Brazilian format)
const formatPriceInput = (value) => {
  if (!value && value !== 0) return '';
  const num = typeof value === 'number' ? value : parsePriceBR(value);
  if (num === 0 && !value) return '';
  // Format as Brazilian: 2299.90 -> "2.299,90"
  return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const initialFormData = {
  name: '',
  description: '',
  image_url: '',
  images: [],
  old_price: '',
  current_price: '',
  affiliate_link: '',
  platform: 'shopee',
  category_id: '',
  is_offer: false,
  is_featured: false,
  is_viral: false,
  is_active: true,
  offer_expires_at: '',
  scheduled_start_date: '',
  display_order: 0,
  tags: [],
};

export const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [platforms, setPlatforms] = useState(defaultPlatforms);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [scrapeUrl, setScrapeUrl] = useState('');
  const [scraping, setScraping] = useState(false);
  const [scrapePreview, setScrapePreview] = useState(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [productsRes, categoriesRes, platformsRes] = await Promise.all([
        productsAPI.getAll({ is_active: null, limit: 500 }),
        categoriesAPI.getAll(),
        platformsAPI.getAll().catch(() => ({ data: [] })),
      ]);
      setProducts(productsRes.data || []);
      setCategories(categoriesRes.data || []);
      if (platformsRes.data?.length > 0) {
        setPlatforms(platformsRes.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort products
  const filteredProducts = useMemo(() => {
    let result = [...products];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(p => 
        p.name.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query)
      );
    }

    // Category filter
    if (filterCategory !== 'all') {
      result = result.filter(p => p.category_id === filterCategory);
    }

    // Platform filter
    if (filterPlatform !== 'all') {
      result = result.filter(p => p.platform === filterPlatform);
    }

    // Status filter
    if (filterStatus === 'active') {
      result = result.filter(p => p.is_active);
    } else if (filterStatus === 'inactive') {
      result = result.filter(p => !p.is_active);
    } else if (filterStatus === 'offer') {
      result = result.filter(p => p.is_offer);
    } else if (filterStatus === 'featured') {
      result = result.filter(p => p.is_featured);
    } else if (filterStatus === 'viral') {
      result = result.filter(p => p.is_viral);
    }

    // Sort
    result.sort((a, b) => {
      let aVal, bVal;
      switch (sortBy) {
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case 'price':
          aVal = a.current_price;
          bVal = b.current_price;
          break;
        case 'clicks':
          aVal = a.click_count || 0;
          bVal = b.click_count || 0;
          break;
        default:
          aVal = new Date(a.created_at);
          bVal = new Date(b.created_at);
      }
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

    return result;
  }, [products, searchQuery, filterCategory, filterPlatform, filterStatus, sortBy, sortOrder]);

  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleOpenDialog = (product = null) => {
    if (product) {
      setSelectedProduct(product);
      setFormData({
        name: product.name || '',
        description: product.description || '',
        image_url: product.image_url || '',
        images: product.images || [],
        old_price: product.old_price ? formatPriceInput(product.old_price) : '',
        current_price: product.current_price ? formatPriceInput(product.current_price) : '',
        affiliate_link: product.affiliate_link || '',
        platform: product.platform || 'shopee',
        category_id: product.category_id || '',
        is_offer: product.is_offer || false,
        is_featured: product.is_featured || false,
        is_viral: product.is_viral || false,
        is_active: product.is_active !== false,
        offer_expires_at: product.offer_expires_at?.slice(0, 16) || '',
        scheduled_start_date: product.scheduled_start_date?.slice(0, 16) || '',
        display_order: product.display_order || 0,
        tags: product.tags || [],
      });
    } else {
      setSelectedProduct(null);
      setFormData(initialFormData);
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.current_price || !formData.affiliate_link) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        old_price: parsePriceBR(formData.old_price) || parsePriceBR(formData.current_price),
        current_price: parsePriceBR(formData.current_price),
        offer_expires_at: formData.offer_expires_at || null,
        scheduled_start_date: formData.scheduled_start_date || null,
      };

      if (selectedProduct) {
        await productsAPI.update(selectedProduct.id, payload);
        toast.success('Produto atualizado com sucesso');
      } else {
        await productsAPI.create(payload);
        toast.success('Produto criado com sucesso');
      }

      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar produto');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await productsAPI.delete(deleteId);
      setProducts(products.filter(p => p.id !== deleteId));
      toast.success('Produto excluído com sucesso');
    } catch (error) {
      toast.error('Erro ao excluir produto');
    } finally {
      setDeleteId(null);
    }
  };

  const handleDuplicate = async (product) => {
    try {
      await productsAPI.duplicate(product.id);
      toast.success('Produto duplicado com sucesso');
      fetchData();
    } catch (error) {
      toast.error('Erro ao duplicar produto');
    }
  };

  const handleToggleStatus = async (product, field) => {
    try {
      await productsAPI.update(product.id, { [field]: !product[field] });
      setProducts(products.map(p => 
        p.id === product.id ? { ...p, [field]: !p[field] } : p
      ));
      toast.success('Status atualizado');
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const response = await uploadImage(file);
      setFormData({ ...formData, image_url: response.data.url });
      toast.success('Imagem enviada');
    } catch (error) {
      toast.error('Erro ao enviar imagem');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleScrapeProduct = async () => {
    if (!scrapeUrl.trim()) {
      toast.error('Cole a URL de qualquer loja (Amazon, Shopee, Magalu, AliExpress, etc.)');
      return;
    }
    setScraping(true);
    setScrapePreview(null);
    try {
      const token = localStorage.getItem('carvalho_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/admin/scrape-product`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ url: scrapeUrl.trim() })
      });
      const data = await response.json();
      if (data.success) {
        // Show preview card instead of applying directly
        setScrapePreview(data);
        if (data.partial && data.error) {
          toast.info(data.error);
        } else if (data.partial) {
          toast.info('Alguns dados nao puderam ser extraidos. Confira e complete manualmente.');
        } else {
          toast.success('Dados extraidos! Confira a previa abaixo.');
        }
      } else {
        toast.error(data.error || 'Erro ao importar dados');
      }
    } catch (error) {
      toast.error('Erro ao conectar com o servidor');
    } finally {
      setScraping(false);
    }
  };

  const applyScrapePreview = () => {
    if (!scrapePreview) return;
    setFormData(prev => ({
      ...prev,
      name: scrapePreview.name || prev.name,
      current_price: scrapePreview.price ? formatPriceInput(scrapePreview.price) : prev.current_price,
      old_price: scrapePreview.old_price ? formatPriceInput(scrapePreview.old_price) : prev.old_price,
      image_url: scrapePreview.image_url || prev.image_url,
      platform: scrapePreview.platform || prev.platform,
      affiliate_link: scrapePreview.clean_url || prev.affiliate_link,
    }));
    toast.success('Dados aplicados ao formulario!');
    setScrapePreview(null);
    setScrapeUrl('');
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilterCategory('all');
    setFilterPlatform('all');
    setFilterStatus('all');
    setCurrentPage(1);
  };

  const getCategoryName = (id) => {
    return categories.find(c => c.id === id)?.name || '-';
  };

  const getPlatformInfo = (slug) => {
    return platforms.find(p => p.slug === slug) || { name: slug, color: '#888' };
  };

  return (
    <AdminLayout title="Produtos">
      <div className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Total</p>
              <p className="text-2xl font-bold">{products.length}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Ativos</p>
              <p className="text-2xl font-bold text-green-600">
                {products.filter(p => p.is_active).length}
              </p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Ofertas</p>
              <p className="text-2xl font-bold text-orange-600">
                {products.filter(p => p.is_offer).length}
              </p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Destaques</p>
              <p className="text-2xl font-bold text-purple-600">
                {products.filter(p => p.is_featured).length}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Buscar produtos..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                  className="pl-10"
                />
              </div>

              {/* Category Filter */}
              <Select value={filterCategory} onValueChange={(v) => { setFilterCategory(v); setCurrentPage(1); }}>
                <SelectTrigger className="w-full lg:w-44">
                  <SelectValue placeholder="Categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas categorias</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Platform Filter */}
              <Select value={filterPlatform} onValueChange={(v) => { setFilterPlatform(v); setCurrentPage(1); }}>
                <SelectTrigger className="w-full lg:w-44">
                  <SelectValue placeholder="Plataforma" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas plataformas</SelectItem>
                  {platforms.map(plat => (
                    <SelectItem key={plat.slug} value={plat.slug}>{plat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Status Filter */}
              <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); setCurrentPage(1); }}>
                <SelectTrigger className="w-full lg:w-36">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="active">Ativos</SelectItem>
                  <SelectItem value="inactive">Inativos</SelectItem>
                  <SelectItem value="offer">Ofertas</SelectItem>
                  <SelectItem value="featured">Destaques</SelectItem>
                  <SelectItem value="viral">Virais</SelectItem>
                </SelectContent>
              </Select>

              {/* Sort */}
              <Select value={`${sortBy}-${sortOrder}`} onValueChange={(v) => { 
                const [by, order] = v.split('-');
                setSortBy(by);
                setSortOrder(order);
              }}>
                <SelectTrigger className="w-full lg:w-40">
                  <SelectValue placeholder="Ordenar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at-desc">Mais recentes</SelectItem>
                  <SelectItem value="created_at-asc">Mais antigos</SelectItem>
                  <SelectItem value="name-asc">Nome A-Z</SelectItem>
                  <SelectItem value="name-desc">Nome Z-A</SelectItem>
                  <SelectItem value="price-asc">Menor preço</SelectItem>
                  <SelectItem value="price-desc">Maior preço</SelectItem>
                  <SelectItem value="clicks-desc">Mais cliques</SelectItem>
                </SelectContent>
              </Select>

              {/* Actions */}
              <div className="flex gap-2">
                <Button variant="outline" size="icon" onClick={clearFilters} title="Limpar filtros">
                  <X className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={fetchData} disabled={loading}>
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
                <Button onClick={() => handleOpenDialog()} className="bg-orange-500 hover:bg-orange-600">
                  <Plus className="w-4 h-4 mr-2" />
                  Novo Produto
                </Button>
              </div>
            </div>

            {/* Active filters */}
            {(searchQuery || filterCategory !== 'all' || filterPlatform !== 'all' || filterStatus !== 'all') && (
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-100">
                <span className="text-sm text-slate-500">Filtros:</span>
                {searchQuery && (
                  <Badge variant="secondary" className="gap-1">
                    Busca: {searchQuery}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => setSearchQuery('')} />
                  </Badge>
                )}
                {filterCategory !== 'all' && (
                  <Badge variant="secondary" className="gap-1">
                    {getCategoryName(filterCategory)}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => setFilterCategory('all')} />
                  </Badge>
                )}
                {filterPlatform !== 'all' && (
                  <Badge variant="secondary" className="gap-1">
                    {getPlatformInfo(filterPlatform).name}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => setFilterPlatform('all')} />
                  </Badge>
                )}
                {filterStatus !== 'all' && (
                  <Badge variant="secondary" className="gap-1">
                    {filterStatus}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => setFilterStatus('all')} />
                  </Badge>
                )}
                <span className="text-sm text-slate-400 ml-auto">
                  {filteredProducts.length} de {products.length} produtos
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Products Table */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
              </div>
            ) : paginatedProducts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                <ImageIcon className="w-12 h-12 mb-4 opacity-50" />
                <p className="font-medium">Nenhum produto encontrado</p>
                <p className="text-sm">Tente ajustar os filtros ou criar um novo produto</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Produto</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Categoria</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Plataforma</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Preço</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Cliques</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Status</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedProducts.map((product) => {
                      const platformInfo = getPlatformInfo(product.platform);
                      return (
                        <tr key={product.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              {product.image_url ? (
                                <img 
                                  src={product.image_url} 
                                  alt={product.name}
                                  className="w-12 h-12 rounded-lg object-cover bg-slate-100"
                                />
                              ) : (
                                <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                                  <ImageIcon className="w-5 h-5 text-slate-400" />
                                </div>
                              )}
                              <div className="min-w-0">
                                <p className="font-medium text-slate-900 truncate max-w-[200px]">{product.name}</p>
                                <p className="text-xs text-slate-400">{formatDate(product.created_at)}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-sm text-slate-600">{getCategoryName(product.category_id)}</span>
                          </td>
                          <td className="py-3 px-4">
                            <Badge 
                              variant="secondary" 
                              style={{ backgroundColor: `${platformInfo.color}20`, color: platformInfo.color }}
                            >
                              {platformInfo.name}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <div>
                              {product.old_price > product.current_price && (
                                <p className="text-xs text-slate-400 line-through">{formatPrice(product.old_price)}</p>
                              )}
                              <p className="font-semibold text-slate-900">{formatPrice(product.current_price)}</p>
                              {product.discount_percent > 0 && (
                                <span className="text-xs text-green-600 font-medium">-{product.discount_percent}%</span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className="text-sm font-medium text-slate-600">{product.click_count || 0}</span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-center gap-1">
                              {product.is_active ? (
                                <Badge variant="secondary" className="bg-green-100 text-green-700">Ativo</Badge>
                              ) : (
                                <Badge variant="secondary" className="bg-slate-100 text-slate-500">Inativo</Badge>
                              )}
                              {product.is_offer && (
                                <Zap className="w-4 h-4 text-orange-500" title="Oferta" />
                              )}
                              {product.is_featured && (
                                <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" title="Destaque" />
                              )}
                              {product.is_viral && (
                                <TrendingUp className="w-4 h-4 text-purple-500" title="Viral" />
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleOpenDialog(product)}>
                                  <Pencil className="w-4 h-4 mr-2" />
                                  Editar
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => window.open(`/ofertas?q=${encodeURIComponent(product.name.split(' ').slice(0, 3).join(' '))}`, '_blank')}>
                                  <Eye className="w-4 h-4 mr-2" />
                                  Ver no Site
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDuplicate(product)}>
                                  <Copy className="w-4 h-4 mr-2" />
                                  Duplicar
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleToggleStatus(product, 'is_active')}>
                                  {product.is_active ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                                  {product.is_active ? 'Desativar' : 'Ativar'}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleToggleStatus(product, 'is_offer')}>
                                  <Zap className="w-4 h-4 mr-2" />
                                  {product.is_offer ? 'Remover Oferta' : 'Marcar Oferta'}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleToggleStatus(product, 'is_featured')}>
                                  <Star className="w-4 h-4 mr-2" />
                                  {product.is_featured ? 'Remover Destaque' : 'Marcar Destaque'}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleToggleStatus(product, 'is_viral')}>
                                  <TrendingUp className="w-4 h-4 mr-2" />
                                  {product.is_viral ? 'Remover Viral' : 'Marcar Viral'}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => setDeleteId(product.id)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Excluir
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between p-4 border-t border-slate-100">
                <p className="text-sm text-slate-500">
                  Mostrando {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, filteredProducts.length)} de {filteredProducts.length}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-slate-600">
                    {currentPage} de {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {selectedProduct ? 'Editar Produto' : 'Novo Produto'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Import via Link - Scraper Inteligente */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                <Label className="text-blue-700 font-semibold flex items-center gap-2">
                  <LinkIcon className="w-4 h-4" />
                  Importar via Link
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={scrapeUrl}
                    onChange={(e) => setScrapeUrl(e.target.value)}
                    placeholder="Cole aqui a URL de qualquer loja (Amazon, Shopee, Magalu, AliExpress...)"
                    className="flex-1 border-blue-200 focus:border-blue-500"
                    data-testid="scrape-url-input"
                  />
                  <Button
                    type="button"
                    onClick={handleScrapeProduct}
                    disabled={scraping}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="scrape-btn"
                  >
                    {scraping ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Importar'}
                  </Button>
                </div>
                <p className="text-xs text-blue-500">Extrai automaticamente titulo, preco e imagem com IA. Funciona com Amazon, Mercado Livre, Shopee, AliExpress, Magalu, Shein, Temu e mais. Aceita links curtos (meli.la, amzn.to).</p>

                {/* Preview Card */}
                {scrapePreview && (
                  <div className="bg-white border border-blue-300 rounded-xl p-4 space-y-3 mt-3" data-testid="scrape-preview-card">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
                        <Eye className="w-4 h-4 text-blue-600" />
                        Previa dos dados extraidos
                      </h4>
                      {scrapePreview.partial && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">Parcial — complete abaixo</span>
                      )}
                    </div>
                    {scrapePreview.error && (
                      <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">{scrapePreview.error}</p>
                    )}
                    <div className="flex gap-4">
                      {/* Image preview */}
                      <div className="flex-shrink-0">
                        {scrapePreview.image_url ? (
                          <div className="relative">
                            <img
                              src={scrapePreview.image_url}
                              alt="Preview"
                              className="w-24 h-24 object-cover rounded-lg border border-green-300 bg-slate-50"
                              data-testid="scrape-preview-image"
                            />
                            <span className="absolute -top-1 -right-1 bg-green-500 text-white w-4 h-4 rounded-full flex items-center justify-center text-[10px]">✓</span>
                          </div>
                        ) : (
                          <div className="w-24 h-24 bg-amber-50 rounded-lg border-2 border-dashed border-amber-300 flex flex-col items-center justify-center gap-1">
                            <ImageIcon className="w-6 h-6 text-amber-400" />
                            <span className="text-[9px] text-amber-500 font-medium">Sem imagem</span>
                          </div>
                        )}
                      </div>
                      {/* Data preview */}
                      <div className="flex-1 min-w-0 space-y-1.5">
                        <div>
                          <span className="text-[10px] uppercase text-slate-400 font-semibold flex items-center gap-1">
                            Titulo {scrapePreview.name ? <span className="text-green-500">✓</span> : <span className="text-amber-500">⚠ manual</span>}
                          </span>
                          <p className={`text-sm truncate font-medium ${scrapePreview.name ? 'text-slate-800' : 'text-amber-500 italic'}`} data-testid="scrape-preview-name">
                            {scrapePreview.name || 'Preencha manualmente'}
                          </p>
                        </div>
                        <div className="flex gap-4">
                          <div>
                            <span className="text-[10px] uppercase text-slate-400 font-semibold flex items-center gap-1">
                              Preco {scrapePreview.price != null ? <span className="text-green-500">✓</span> : <span className="text-amber-500">⚠ manual</span>}
                            </span>
                            <p className={`text-sm font-bold ${scrapePreview.price != null ? 'text-green-600' : 'text-amber-500 italic'}`} data-testid="scrape-preview-price">
                              {scrapePreview.price != null ? `R$ ${scrapePreview.price.toFixed(2).replace('.', ',')}` : 'Preencha manualmente'}
                            </p>
                          </div>
                          {scrapePreview.old_price != null && (
                            <div>
                              <span className="text-[10px] uppercase text-slate-400 font-semibold">Preco Original <span className="text-green-500">✓</span></span>
                              <p className="text-sm text-slate-500 line-through">
                                R$ {scrapePreview.old_price.toFixed(2).replace('.', ',')}
                              </p>
                            </div>
                          )}
                        </div>
                        <div className="flex gap-4">
                          {scrapePreview.platform && (
                            <div>
                              <span className="text-[10px] uppercase text-slate-400 font-semibold">Plataforma <span className="text-green-500">✓</span></span>
                              <p className="text-sm text-slate-700 capitalize" data-testid="scrape-preview-platform">{scrapePreview.platform}</p>
                            </div>
                          )}
                          {scrapePreview.clean_url && (
                            <div className="flex-1 min-w-0">
                              <span className="text-[10px] uppercase text-slate-400 font-semibold">Link Limpo <span className="text-green-500">✓</span></span>
                              <p className="text-xs text-blue-600 truncate">{scrapePreview.clean_url}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <Button
                        type="button"
                        onClick={applyScrapePreview}
                        className="bg-green-600 hover:bg-green-700 text-white flex-1"
                        data-testid="scrape-apply-btn"
                      >
                        <CheckCircle className="w-4 h-4 mr-1.5" />
                        {scrapePreview.partial ? 'Aplicar e Completar' : 'Aplicar Dados'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setScrapePreview(null)}
                        className="text-slate-600"
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2 space-y-2">
                  <Label>Nome *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Nome do produto"
                    required
                  />
                </div>

                <div className="md:col-span-2 space-y-2">
                  <Label>Descrição</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descrição do produto"
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Preço Original</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    data-testid="product-old-price-input"
                    value={formData.old_price}
                    onChange={(e) => {
                      // Allow digits, dots, and commas for Brazilian format
                      const val = e.target.value.replace(/[^\d.,]/g, '');
                      setFormData({ ...formData, old_price: val });
                    }}
                    onBlur={(e) => {
                      // Format on blur
                      const parsed = parsePriceBR(e.target.value);
                      if (parsed > 0) {
                        setFormData(prev => ({ ...prev, old_price: formatPriceInput(parsed) }));
                      }
                    }}
                    placeholder="Ex: 2.299,90"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Preço Atual *</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    data-testid="product-current-price-input"
                    value={formData.current_price}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^\d.,]/g, '');
                      setFormData({ ...formData, current_price: val });
                    }}
                    onBlur={(e) => {
                      const parsed = parsePriceBR(e.target.value);
                      if (parsed > 0) {
                        setFormData(prev => ({ ...prev, current_price: formatPriceInput(parsed) }));
                      }
                    }}
                    placeholder="Ex: 1.251,00"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Categoria</Label>
                  <Select 
                    value={formData.category_id} 
                    onValueChange={(v) => setFormData({ ...formData, category_id: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Plataforma</Label>
                  <Select 
                    value={formData.platform} 
                    onValueChange={(v) => setFormData({ ...formData, platform: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      {platforms.map(plat => (
                        <SelectItem key={plat.slug} value={plat.slug}>{plat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="md:col-span-2 space-y-2">
                  <Label>Link de Afiliado *</Label>
                  <Input
                    value={formData.affiliate_link}
                    onChange={(e) => setFormData({ ...formData, affiliate_link: e.target.value })}
                    placeholder="https://..."
                    required
                  />
                </div>

                <div className="md:col-span-2 space-y-2">
                  <Label>URL da Imagem</Label>
                  <div className="flex gap-2">
                    <Input
                      value={formData.image_url}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      placeholder="https://... ou faça upload"
                      className="flex-1"
                    />
                    <div className="relative">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                      <Button type="button" variant="outline" disabled={uploadingImage}>
                        {uploadingImage ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Upload'}
                      </Button>
                    </div>
                  </div>
                  {formData.image_url && (
                    <img src={formData.image_url} alt="Preview" className="w-20 h-20 object-cover rounded-lg mt-2" />
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Expiracao da Oferta</Label>
                  <Input
                    type="datetime-local"
                    value={formData.offer_expires_at}
                    onChange={(e) => setFormData({ ...formData, offer_expires_at: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    Data de Inicio (Agendamento)
                  </Label>
                  <Input
                    type="datetime-local"
                    value={formData.scheduled_start_date || ''}
                    onChange={(e) => setFormData({ ...formData, scheduled_start_date: e.target.value })}
                  />
                  <p className="text-xs text-slate-500">Produto so aparece no site apos esta data</p>
                </div>
              </div>

              {/* Toggles */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                  />
                  <Label className="font-normal">Ativo</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_offer}
                    onCheckedChange={(v) => setFormData({ ...formData, is_offer: v })}
                  />
                  <Label className="font-normal">Oferta</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_featured}
                    onCheckedChange={(v) => setFormData({ ...formData, is_featured: v })}
                  />
                  <Label className="font-normal">Destaque</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_viral}
                    onCheckedChange={(v) => setFormData({ ...formData, is_viral: v })}
                  />
                  <Label className="font-normal">Viral</Label>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={saving} className="bg-orange-500 hover:bg-orange-600">
                  {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
                  {selectedProduct ? 'Atualizar' : 'Criar'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Excluir produto?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta ação não pode ser desfeita. O produto será removido permanentemente.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default AdminProducts;
